import React, { useEffect, useRef } from 'react';
import { withInject } from '@piwikpro/platform';
import classNameBind from 'classnames/bind';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import styles from './CheckoutIframeOverlay.module.css';

const bindClasses = classNameBind.bind(styles);

interface InjectedProps {
  productAnalytics: ProductAnalytics
}

interface CheckoutIframeOverlayProps extends InjectedProps {
  checkoutUrl: string
  onClose: () => void
  onSuccess?: () => void
}

const CheckoutIframeOverlay: React.FC<CheckoutIframeOverlayProps> = ({
  checkoutUrl,
  onClose,
  onSuccess,
  productAnalytics,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = [
        'https://success.piwik.pro',
        window.location.origin,
      ];

      if (!allowedOrigins.includes(event.origin) || event.data?.source !== 'paddle-checkout') {
        return;
      }

      productAnalytics.push({
        type: `@platform/command/payments/paddleCheckout/${event.data?.type}`,
      });

      if (event.data?.type === 'checkout.completed') {
        onSuccess?.();
        onClose();
      } else if (event.data?.type === 'checkout.closed') {
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onClose, onSuccess]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={bindClasses('overlay')} onClick={handleBackdropClick}>
      <div className={bindClasses('container')}>
        <iframe
          ref={iframeRef}
          src={checkoutUrl}
          className={bindClasses('iframe')}
          title="Checkout"
        />
      </div>
    </div>
  );
};

export default withInject<InjectedProps>({
  productAnalytics: 'productAnalytics',
})(CheckoutIframeOverlay);
